/**
 * Data Onboarding API Service
 * 
 * Handles all API calls for:
 * - Data Import (file uploads, previews, execution)
 * - Integrations (connect, disconnect, sync third-party systems)
 * - Migration (from legacy systems to ASCIN)
 * 
 * UK formatting standards applied throughout.
 */

import axios, { AxiosInstance } from 'axios';

// API Base URL - uses port 8005 for ASCIN backend
const API_BASE_URL = process.env.REACT_APP_API_URL || '';

// Create axios instance with defaults
const api: AxiosInstance = axios.create({
  baseURL: `${API_BASE_URL}/api/v1`,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 seconds for file uploads
});

// Add auth token to requests if available
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ============================================
// TYPE DEFINITIONS
// ============================================

export interface FieldMapping {
  source_field: string;
  target_field: string;
  transform?: string;
}

export interface ImportConfig {
  data_type: 'products' | 'suppliers' | 'inventory' | 'components' | 'warehouses' | 'locations';
  field_mappings: FieldMapping[];
  skip_header: boolean;
  date_format: string;
  currency: string;
}

export interface ImportJob {
  id: string;
  filename: string;
  file_type: string;
  data_type: string;
  file_size: number;
  status: 'uploaded' | 'previewed' | 'processing' | 'completed' | 'failed';
  created_at: string;
  completed_at?: string;
  preview_data?: Record<string, unknown>[];
  validation?: ValidationResult;
  columns?: string[];
  result?: ImportResult;
}

export interface ValidationResult {
  valid: boolean;
  errors: ValidationError[];
  warnings: ValidationError[];
  total_rows: number;
  error_count: number;
  warning_count: number;
}

export interface ValidationError {
  row: number;
  field: string;
  message: string;
}

export interface ImportResult {
  imported: number;
  skipped: number;
  errors: number;
}

export interface ImportTemplate {
  columns: string[];
  sample: Record<string, string>[];
  required: string[];
  date_format: string;
  currency: string;
}

export interface Integration {
  id: string;
  name: string;
  region: string;
  description: string;
  auth_type: 'api_key' | 'oauth';
  features: string[];
  setup_url: string;
  configured: boolean;
  status: 'connected' | 'disconnected' | 'syncing' | 'error';
  last_sync?: string;
}

export interface IntegrationConfig {
  integration_id: string;
  api_key?: string;
  api_secret?: string;
  oauth_token?: string;
  base_url?: string;
  sync_frequency: 'hourly' | 'daily' | 'weekly';
  sync_products: boolean;
  sync_inventory: boolean;
  sync_orders: boolean;
  sync_suppliers: boolean;
  field_mappings?: Record<string, string>;
}

export interface SyncResult {
  products_synced: number;
  inventory_updated: number;
  orders_imported: number;
  errors: number;
}

export interface SyncHistoryItem {
  id: string;
  started_at: string;
  completed_at: string;
  status: 'completed' | 'failed' | 'partial';
  products_synced: number;
  inventory_updated: number;
  orders_imported: number;
  errors: number;
}

export interface MigrationConfig {
  source_type: 'erp' | 'spreadsheets' | 'wms' | 'legacy' | 'custom';
  source_name: string;
  connection_string?: string;
  api_endpoint?: string;
  credentials?: Record<string, string>;
  data_types: string[];
  field_mappings: Record<string, FieldMapping[]>;
}

export interface MigrationJob {
  id: string;
  source_type: string;
  source_name: string;
  data_types: string[];
  status: 'initializing' | 'connecting' | 'extracting' | 'transforming' | 'completed' | 'cancelled' | 'failed';
  created_at: string;
  completed_at?: string;
  progress: {
    current_step: number;
    total_steps: number;
    step_name: string;
    percentage: number;
  };
  results?: MigrationResult;
}

export interface MigrationResult {
  products_migrated: number;
  suppliers_migrated: number;
  inventory_records: number;
  errors: number;
  warnings: number;
}

// ============================================
// DATA IMPORT API
// ============================================

export const importApi = {
  /**
   * Upload a file for import
   */
  async uploadFile(file: File, dataType: string): Promise<ImportJob> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('data_type', dataType);

    const response = await api.post('/import/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  /**
   * Preview uploaded file data
   */
  async previewImport(importId: string): Promise<{
    import_id: string;
    data_type: string;
    columns: string[];
    preview_rows: Record<string, unknown>[];
    total_rows: number;
    validation: ValidationResult;
    suggested_mappings: Record<string, string>;
  }> {
    const response = await api.post(`/import/preview/${importId}`);
    return response.data;
  },

  /**
   * Execute the import with configuration
   */
  async executeImport(importId: string, config: ImportConfig): Promise<{
    import_id: string;
    status: string;
    result: ImportResult;
    message: string;
  }> {
    const response = await api.post(`/import/execute/${importId}`, config);
    return response.data;
  },

  /**
   * Get import job status
   */
  async getStatus(importId: string): Promise<ImportJob> {
    const response = await api.get(`/import/status/${importId}`);
    return response.data;
  },

  /**
   * Get import history
   */
  async getHistory(skip = 0, limit = 20): Promise<{
    items: ImportJob[];
    total: number;
  }> {
    const response = await api.get('/import/history', {
      params: { skip, limit },
    });
    return response.data;
  },

  /**
   * Get import template for a data type
   */
  async getTemplate(dataType: string): Promise<ImportTemplate> {
    const response = await api.get(`/import/templates/${dataType}`);
    return response.data;
  },
};

// ============================================
// INTEGRATIONS API
// ============================================

export const integrationsApi = {
  /**
   * List all available integrations
   */
  async listIntegrations(): Promise<{
    integrations: Integration[];
    total: number;
    connected: number;
  }> {
    const response = await api.get('/integrations');
    return response.data;
  },

  /**
   * Get integration details
   */
  async getIntegration(integrationId: string): Promise<Integration & {
    sync_settings: Record<string, unknown>;
    field_mappings: Record<string, string>;
  }> {
    const response = await api.get(`/integrations/${integrationId}`);
    return response.data;
  },

  /**
   * Connect an integration
   */
  async connect(config: IntegrationConfig): Promise<{
    integration_id: string;
    status: string;
    message: string;
    next_sync: string;
  }> {
    const response = await api.post(`/integrations/${config.integration_id}/connect`, config);
    return response.data;
  },

  /**
   * Disconnect an integration
   */
  async disconnect(integrationId: string): Promise<{
    integration_id: string;
    status: string;
    message: string;
  }> {
    const response = await api.post(`/integrations/${integrationId}/disconnect`);
    return response.data;
  },

  /**
   * Trigger manual sync
   */
  async syncNow(integrationId: string): Promise<{
    integration_id: string;
    status: string;
    result: SyncResult;
    message: string;
  }> {
    const response = await api.post(`/integrations/${integrationId}/sync`);
    return response.data;
  },

  /**
   * Get sync history
   */
  async getSyncHistory(integrationId: string, limit = 10): Promise<{
    integration_id: string;
    history: SyncHistoryItem[];
  }> {
    const response = await api.get(`/integrations/${integrationId}/history`, {
      params: { limit },
    });
    return response.data;
  },
};

// ============================================
// MIGRATION API
// ============================================

export const migrationApi = {
  /**
   * Start a new migration
   */
  async startMigration(config: MigrationConfig): Promise<{
    migration_id: string;
    status: string;
    message: string;
  }> {
    const response = await api.post('/migration/start', config);
    return response.data;
  },

  /**
   * Get migration status
   */
  async getStatus(migrationId: string): Promise<MigrationJob> {
    const response = await api.get(`/migration/${migrationId}/status`);
    return response.data;
  },

  /**
   * Cancel a running migration
   */
  async cancelMigration(migrationId: string): Promise<{
    migration_id: string;
    status: string;
    message: string;
  }> {
    const response = await api.post(`/migration/${migrationId}/cancel`);
    return response.data;
  },

  /**
   * Get migration history
   */
  async getHistory(skip = 0, limit = 20): Promise<{
    items: MigrationJob[];
    total: number;
  }> {
    const response = await api.get('/migration/history', {
      params: { skip, limit },
    });
    return response.data;
  },

  /**
   * Test connection to migration source
   */
  async testConnection(
    sourceType: string,
    connectionString?: string,
    apiEndpoint?: string
  ): Promise<{
    status: string;
    message: string;
    details: {
      latency_ms: number;
      version: string;
      features_available: string[];
    };
  }> {
    const formData = new FormData();
    formData.append('source_type', sourceType);
    if (connectionString) formData.append('connection_string', connectionString);
    if (apiEndpoint) formData.append('api_endpoint', apiEndpoint);

    const response = await api.post('/migration/test-connection', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
};

// ============================================
// UK FORMATTING API
// ============================================

export const formattingApi = {
  /**
   * Get UK formatting standards
   */
  async getStandards(): Promise<{
    date_format: string;
    time_format: string;
    currency: {
      code: string;
      symbol: string;
      decimal_places: number;
      thousands_separator: string;
      decimal_separator: string;
    };
    phone_format: string;
    postcode_format: string;
    terminology: Record<string, string>;
  }> {
    const response = await api.get('/formatting/standards');
    return response.data;
  },
};

// Export all APIs as default
export default {
  import: importApi,
  integrations: integrationsApi,
  migration: migrationApi,
  formatting: formattingApi,
};
